import React from 'react';
import { clsx } from 'clsx';
import { AlertCircle, CheckCircle, Info, AlertTriangle, X } from 'lucide-react';

export interface AlertProps {
  severity?: 'success' | 'info' | 'warning' | 'error';
  variant?: 'filled' | 'outlined' | 'standard';
  onClose?: () => void;
  children: React.ReactNode;
  className?: string;
}

const Alert: React.FC<AlertProps> = ({
  severity = 'info',
  variant = 'standard',
  onClose,
  children,
  className,
}) => {
  const icons = {
    success: CheckCircle,
    info: Info,
    warning: AlertTriangle,
    error: AlertCircle,
  };

  const Icon = icons[severity];

  const classes = clsx(
    'flex items-start gap-3 p-4 rounded border',
    {
      // Filled variants
      'bg-success text-white border-success': variant === 'filled' && severity === 'success',
      'bg-info text-white border-info': variant === 'filled' && severity === 'info',
      'bg-warning text-white border-warning': variant === 'filled' && severity === 'warning',
      'bg-error text-white border-error': variant === 'filled' && severity === 'error',

      // Outlined variants
      'bg-white text-success-700 border-success': variant === 'outlined' && severity === 'success',
      'bg-white text-info-700 border-info': variant === 'outlined' && severity === 'info',
      'bg-white text-warning-700 border-warning': variant === 'outlined' && severity === 'warning',
      'bg-white text-error-700 border-error': variant === 'outlined' && severity === 'error',

      // Standard variants
      'bg-success-50 text-success-800 border-success-200': variant === 'standard' && severity === 'success',
      'bg-info-50 text-info-800 border-info-200': variant === 'standard' && severity === 'info',
      'bg-warning-50 text-warning-800 border-warning-200': variant === 'standard' && severity === 'warning',
      'bg-error-50 text-error-800 border-error-200': variant === 'standard' && severity === 'error',
    },
    className
  );

  return (
    <div className={classes}>
      <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
      <div className="flex-1">{children}</div>
      {onClose && (
        <button
          onClick={onClose}
          className="flex-shrink-0 hover:opacity-70 transition-opacity"
        >
          <X className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

export default Alert;

